import JSZip from 'jszip'
import pdfjsLib from '@/utils/pdfjs'

const LEARNING_COVER_EXTENSIONS = ['pdf', 'epub', 'mobi', 'azw', 'azw3']
const IMAGE_MIME_BY_EXTENSION = {
  bmp: 'image/bmp',
  gif: 'image/gif',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  svg: 'image/svg+xml',
  webp: 'image/webp'
}

function getFileExtension(fileName) {
  const value = String(fileName || '').trim().toLowerCase()
  const dotIndex = value.lastIndexOf('.')
  return dotIndex >= 0 && dotIndex < value.length - 1 ? value.slice(dotIndex + 1) : ''
}

function getBaseFileName(fileName) {
  const value = String(fileName || 'cover').trim()
  const dotIndex = value.lastIndexOf('.')
  const base = dotIndex > 0 ? value.slice(0, dotIndex) : value
  return (base || 'cover').replace(/[\\/:*?"<>|]+/g, '_')
}

function buildCoverFileName(fileName, extension) {
  return `${getBaseFileName(fileName)}-cover.${extension || 'jpg'}`
}

function readFileAsArrayBuffer(file) {
  if (file && typeof file.arrayBuffer == 'function') {
    return file.arrayBuffer()
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = event => resolve(event && event.target ? event.target.result : null)
    reader.onerror = () => reject(reader.error || new Error('File read failed'))
    reader.readAsArrayBuffer(file)
  })
}

function createUploadFile(blob, fileName, type) {
  const uploadBlob = blob && blob.type == type ? blob : new Blob([blob], { type: type || (blob && blob.type) || 'application/octet-stream' })
  if (typeof File != 'undefined') {
    return new File([uploadBlob], fileName, { type: uploadBlob.type })
  }
  uploadBlob.name = fileName
  return uploadBlob
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => {
      if (!blob) {
        reject(new Error('Canvas convert failed'))
        return
      }
      resolve(blob)
    }, type, quality)
  })
}

function releaseCanvas(canvas) {
  if (canvas) {
    canvas.width = 0
    canvas.height = 0
  }
}

async function createPdfCoverFile(file, fileName) {
  const bytes = new Uint8Array(await readFileAsArrayBuffer(file))
  let pdfDocument = null
  let canvas = null
  try {
    const loadingTask = pdfjsLib.getDocument({ data: bytes })
    pdfDocument = await loadingTask.promise
    if (!pdfDocument || !pdfDocument.numPages) {
      return null
    }
    const page = await pdfDocument.getPage(1)
    const baseViewport = page.getViewport({ scale: 1 })
    const longSide = Math.max(baseViewport.width || 0, baseViewport.height || 0)
    const scale = longSide > 0 ? Math.min(2, Math.max(0.2, 720 / longSide)) : 1
    const viewport = page.getViewport({ scale })
    const ownerDocument = typeof window != 'undefined' && window.document ? window.document : null
    if (!ownerDocument) {
      return null
    }
    canvas = ownerDocument.createElement('canvas')
    canvas.width = Math.max(1, Math.ceil(viewport.width))
    canvas.height = Math.max(1, Math.ceil(viewport.height))
    const context = canvas.getContext('2d')
    context.fillStyle = '#fff'
    context.fillRect(0, 0, canvas.width, canvas.height)
    await page.render({ canvasContext: context, viewport }).promise
    const blob = await canvasToBlob(canvas, 'image/jpeg', 0.86)
    return createUploadFile(blob, buildCoverFileName(fileName, 'jpg'), 'image/jpeg')
  } finally {
    releaseCanvas(canvas)
    if (pdfDocument && typeof pdfDocument.destroy == 'function') {
      await pdfDocument.destroy()
    }
  }
}

function decodeXmlEntities(value) {
  return String(value || '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#39;/g, "'")
}

function parseXmlAttributes(tag) {
  const attrs = {}
  String(tag || '').replace(/([:\w-]+)\s*=\s*(["'])(.*?)\2/g, (match, name, quote, value) => {
    attrs[String(name || '').toLowerCase()] = decodeXmlEntities(value)
    return match
  })
  return attrs
}

function normalizeZipPath(path) {
  return String(path || '').replace(/\\/g, '/').replace(/^\/+/, '')
}

function joinZipPath(basePath, href) {
  const rawHref = String(href || '').split('#')[0].split('?')[0]
  const isAbsolute = /^[\\/]+/.test(rawHref)
  const cleanHref = normalizeZipPath(rawHref)
  if (!cleanHref) {
    return ''
  }
  const normalizedBase = normalizeZipPath(basePath)
  const baseDir = normalizedBase.includes('/') ? normalizedBase.slice(0, normalizedBase.lastIndexOf('/')) : ''
  const rawPath = isAbsolute ? cleanHref : [baseDir, cleanHref].filter(Boolean).join('/')
  const parts = []
  rawPath.split('/').forEach(part => {
    if (!part || part == '.') {
      return
    }
    if (part == '..') {
      parts.pop()
      return
    }
    parts.push(part)
  })
  return parts.join('/')
}

function getZipFile(zip, path) {
  const normalized = normalizeZipPath(path)
  if (!normalized) {
    return null
  }
  if (zip.files[normalized]) {
    return zip.files[normalized]
  }
  const lower = normalized.toLowerCase()
  const name = Object.keys(zip.files || {}).find(item => normalizeZipPath(item).toLowerCase() == lower)
  return name ? zip.files[name] : null
}

async function readZipFileText(zip, path) {
  const file = getZipFile(zip, path)
  return file && !file.dir ? await file.async('string') : ''
}

function findZipPath(zip, pattern) {
  const names = Object.keys(zip && zip.files ? zip.files : {})
  const name = names.find(item => {
    const file = zip.files[item]
    return file && !file.dir && pattern.test(normalizeZipPath(item))
  })
  return name || ''
}

function extractOpfPath(containerText) {
  const match = String(containerText || '').match(/<rootfile\b[^>]*\bfull-path\s*=\s*(["'])(.*?)\1[^>]*>/i)
  return match ? decodeXmlEntities(match[2]) : ''
}

function parseOpfManifest(opfText, opfPath) {
  const items = []
  String(opfText || '').replace(/<item\b[^>]*>/gi, tag => {
    const attrs = parseXmlAttributes(tag)
    if (attrs.id && attrs.href) {
      items.push({
        id: attrs.id,
        href: attrs.href,
        path: joinZipPath(opfPath, attrs.href),
        mediaType: attrs['media-type'] || '',
        properties: attrs.properties || ''
      })
    }
    return tag
  })
  return items
}

function extractEpubCoverId(opfText) {
  let coverId = ''
  String(opfText || '').replace(/<meta\b[^>]*>/gi, tag => {
    if (coverId) {
      return tag
    }
    const attrs = parseXmlAttributes(tag)
    if (String(attrs.name || '').toLowerCase() == 'cover' && attrs.content) {
      coverId = attrs.content
    }
    return tag
  })
  return coverId
}

function isEpubImageItem(item) {
  const extension = getFileExtension(item && item.path)
  return !!(item && (/^image\//i.test(item.mediaType) || IMAGE_MIME_BY_EXTENSION[extension]))
}

function pickEpubCoverItem(manifest, opfText) {
  const coverId = extractEpubCoverId(opfText)
  return manifest.find(item => isEpubImageItem(item) && /\bcover-image\b/i.test(item.properties))
    || manifest.find(item => coverId && item.id == coverId && isEpubImageItem(item))
    || manifest.find(item => isEpubImageItem(item) && /(?:^|[/_-])cover(?:[/_.-]|$)/i.test(item.path))
    || manifest.find(item => isEpubImageItem(item) && /cover/i.test(item.id))
    || null
}

async function createEpubCoverFile(file, fileName) {
  const zip = await JSZip.loadAsync(await readFileAsArrayBuffer(file))
  const containerText = await readZipFileText(zip, 'META-INF/container.xml')
  const opfPath = extractOpfPath(containerText) || findZipPath(zip, /\.opf$/i)
  const opfText = opfPath ? await readZipFileText(zip, opfPath) : ''
  const manifest = parseOpfManifest(opfText, opfPath)
  const coverItem = pickEpubCoverItem(manifest, opfText)
  if (!coverItem || !coverItem.path) {
    return null
  }
  const coverZipFile = getZipFile(zip, coverItem.path)
  if (!coverZipFile || coverZipFile.dir) {
    return null
  }
  const extension = getFileExtension(coverItem.path) || 'jpg'
  const mimeType = coverItem.mediaType || IMAGE_MIME_BY_EXTENSION[extension] || 'application/octet-stream'
  const blob = await coverZipFile.async('blob')
  return createUploadFile(blob, buildCoverFileName(fileName, extension), mimeType)
}

function readUInt16BE(bytes, offset) {
  if (!bytes || offset < 0 || offset + 2 > bytes.length) {
    return 0
  }
  return (bytes[offset] << 8) | bytes[offset + 1]
}

function readUInt32BE(bytes, offset) {
  if (!bytes || offset < 0 || offset + 4 > bytes.length) {
    return 0
  }
  return ((bytes[offset] * 0x1000000) + ((bytes[offset + 1] << 16) | (bytes[offset + 2] << 8) | bytes[offset + 3])) >>> 0
}

function bytesEqualAscii(bytes, offset, value) {
  if (!bytes || offset < 0 || offset + value.length > bytes.length) {
    return false
  }
  for (let i = 0; i < value.length; i++) {
    if (bytes[offset + i] != value.charCodeAt(i)) {
      return false
    }
  }
  return true
}

function getMobiRecordRanges(bytes) {
  const recordCount = readUInt16BE(bytes, 76)
  if (!recordCount || 78 + recordCount * 8 > bytes.length) {
    return []
  }
  const offsets = []
  for (let i = 0; i < recordCount; i++) {
    const offset = readUInt32BE(bytes, 78 + i * 8)
    if (offset > 0 && offset < bytes.length) {
      offsets.push(offset)
    }
  }
  offsets.sort((a, b) => a - b)
  return offsets.map((offset, index) => ({
    start: offset,
    end: offsets[index + 1] || bytes.length
  })).filter(range => range.end > range.start)
}

function findMobiHeaderOffset(bytes, firstRecord) {
  if (!firstRecord) {
    return -1
  }
  const scanEnd = Math.min(firstRecord.end, firstRecord.start + 512)
  for (let offset = firstRecord.start; offset + 4 <= scanEnd; offset++) {
    if (bytesEqualAscii(bytes, offset, 'MOBI')) {
      return offset
    }
  }
  return -1
}

function getMobiExthRecords(bytes, mobiOffset, firstRecordEnd) {
  const mobiHeaderLength = readUInt32BE(bytes, mobiOffset + 4)
  const expectedOffset = mobiOffset + mobiHeaderLength
  let exthOffset = bytesEqualAscii(bytes, expectedOffset, 'EXTH') ? expectedOffset : -1
  if (exthOffset < 0) {
    const scanEnd = Math.min(firstRecordEnd, expectedOffset + 32)
    for (let offset = mobiOffset + 16; offset + 4 <= scanEnd; offset++) {
      if (bytesEqualAscii(bytes, offset, 'EXTH')) {
        exthOffset = offset
        break
      }
    }
  }
  if (exthOffset < 0) {
    return []
  }
  const recordCount = readUInt32BE(bytes, exthOffset + 8)
  const records = []
  let cursor = exthOffset + 12
  for (let i = 0; i < recordCount && cursor + 8 <= firstRecordEnd; i++) {
    const type = readUInt32BE(bytes, cursor)
    const size = readUInt32BE(bytes, cursor + 4)
    if (size < 8 || cursor + size > firstRecordEnd) {
      break
    }
    records.push({
      type,
      dataStart: cursor + 8,
      dataEnd: cursor + size
    })
    cursor += size
  }
  return records
}

function readExthUInt32(bytes, records, type) {
  const record = records.find(item => item.type == type && item.dataEnd - item.dataStart >= 4)
  return record ? readUInt32BE(bytes, record.dataStart) : 0
}

function detectImageType(bytes, start, end) {
  if (start < 0 || end <= start || end > bytes.length) {
    return null
  }
  if (end - start >= 4 && bytes[start] == 0xff && bytes[start + 1] == 0xd8 && bytes[start + 2] == 0xff) {
    return { extension: 'jpg', mimeType: 'image/jpeg' }
  }
  if (end - start >= 8
    && bytes[start] == 0x89
    && bytes[start + 1] == 0x50
    && bytes[start + 2] == 0x4e
    && bytes[start + 3] == 0x47) {
    return { extension: 'png', mimeType: 'image/png' }
  }
  if (end - start >= 6 && bytesEqualAscii(bytes, start, 'GIF')) {
    return { extension: 'gif', mimeType: 'image/gif' }
  }
  if (end - start >= 12 && bytesEqualAscii(bytes, start, 'RIFF') && bytesEqualAscii(bytes, start + 8, 'WEBP')) {
    return { extension: 'webp', mimeType: 'image/webp' }
  }
  if (end - start >= 2 && bytesEqualAscii(bytes, start, 'BM')) {
    return { extension: 'bmp', mimeType: 'image/bmp' }
  }
  return null
}

function getMobiCoverCandidateIndexes(bytes, records, mobiOffset, exthRecords) {
  const coverOffset = readExthUInt32(bytes, exthRecords, 201) || readExthUInt32(bytes, exthRecords, 202)
  const firstImageIndex = readUInt32BE(bytes, mobiOffset + 0x6c)
  const indexes = []
  if (firstImageIndex > 0 && coverOffset >= 0) {
    indexes.push(firstImageIndex + coverOffset)
  }
  if (coverOffset > 0) {
    indexes.push(coverOffset)
  }
  if (firstImageIndex > 0) {
    indexes.push(firstImageIndex)
  }
  const scanStart = firstImageIndex > 0 && firstImageIndex < records.length ? firstImageIndex : 1
  for (let i = scanStart; i < records.length; i++) {
    indexes.push(i)
  }
  return indexes.filter((index, position) => index > 0 && index < records.length && indexes.indexOf(index) == position)
}

async function createMobiCoverFile(file, fileName) {
  const bytes = new Uint8Array(await readFileAsArrayBuffer(file))
  const records = getMobiRecordRanges(bytes)
  if (records.length == 0) {
    return null
  }
  const mobiOffset = findMobiHeaderOffset(bytes, records[0])
  if (mobiOffset < 0) {
    return null
  }
  const exthRecords = getMobiExthRecords(bytes, mobiOffset, records[0].end)
  const candidateIndexes = getMobiCoverCandidateIndexes(bytes, records, mobiOffset, exthRecords)
  for (let i = 0; i < candidateIndexes.length; i++) {
    const record = records[candidateIndexes[i]]
    const imageType = detectImageType(bytes, record.start, record.end)
    if (imageType) {
      const blob = new Blob([bytes.slice(record.start, record.end)], { type: imageType.mimeType })
      return createUploadFile(blob, buildCoverFileName(fileName, imageType.extension), imageType.mimeType)
    }
  }
  return null
}

export async function createLearningUploadCoverFile(file, fileName) {
  const resolvedFileName = fileName || (file && file.name) || ''
  const extension = getFileExtension(resolvedFileName)
  if (!file || !LEARNING_COVER_EXTENSIONS.includes(extension)) {
    return null
  }
  try {
    if (extension == 'pdf') {
      return await createPdfCoverFile(file, resolvedFileName)
    }
    if (extension == 'epub') {
      return await createEpubCoverFile(file, resolvedFileName)
    }
    return await createMobiCoverFile(file, resolvedFileName)
  } catch (error) {
    if (typeof console != 'undefined' && typeof console.warn == 'function') {
      console.warn('[cloudUploadCover] Failed to create learning upload cover:', error)
    }
    return null
  }
}

export async function createPdfUploadCoverFile(file, fileName) {
  const resolvedFileName = fileName || (file && file.name) || ''
  if (!file || getFileExtension(resolvedFileName) != 'pdf') {
    return null
  }
  try {
    return await createPdfCoverFile(file, resolvedFileName)
  } catch (error) {
    if (typeof console != 'undefined' && typeof console.warn == 'function') {
      console.warn('[cloudUploadCover] Failed to create PDF upload cover:', error)
    }
    return null
  }
}
